<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            `${isAdd ? $t("actions.add") : $t("actions.edit")} ${$t(
              "service_orders.title"
            )}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            :name="$t('service_orders.reference_number')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('service_orders.reference_number')"
              label-for="reference_number"
            >
              <b-form-input
                id="reference_number"
                v-model="itemData.reference_number"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('service_orders.company')"
            rules="required"
          >
            <b-form-group
              :label="$t('service_orders.company')"
              label-for="id_maintenance_company"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_maintenance_company"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="maintenanceOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                input-id="id_maintenance_company"
                :closeOnSelect="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('maintenance.maintenance_crew')"
          >
            <b-form-group
              :label="$t('maintenance.maintenance_crew')"
              label-for="id_maintenance_crew"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_maintenance_crew"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="maintenanceCrewOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                input-id="id_maintenance_crew"
                :closeOnSelect="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('service_orders.facility')"
          >
            <b-form-group
              data-cy="facility"
              class="text-capitalize"
              :label="$t('service_orders.facility')"
              label-for="id_unit"
              :state="getValidationState(validationContext)"
            >
              <select-paginate-unit
                :id_unit.sync="id_unit"
                input-id="id_unit"
                :multiple="isAdd"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('service_orders.attention_date')"
            rules="required"
          >
            <b-form-group
              :label="$t('service_orders.attention_date')"
              label-for="attention_date"
              :state="getValidationState(validationContext)"
            >
              <flat-pickr
                class="form-control"
                v-model="itemData.attention_date"
                @input="(val) => dateStarClick(val)"
                :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('maintenance.template')"
            rules="required"
          >
            <b-form-group
              :label="$t('maintenance.template')"
              label-for="id_maintenance_template"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_maintenance_template"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="maintenanceTemplateOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                input-id="id_maintenance_template"
                :closeOnSelect="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BCol,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import SelectPaginateUnit from "../components/SelectPaginateUnit.vue";
import moment from "moment-timezone";
// import tenant from "@/store/tenant"; // no lo ocupamos de momento

export default {
  components: {
    flatPickr,
    BCol,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
    SelectPaginateUnit,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    unitOptions: {
      type: Array,
      required: true,
    },
    maintenanceOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    maintenanceTemplateOptions: {
      type: Array,
      required: true,
    },
    maintenanceCrewOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
      required_if,
    };
  },
  setup(props, { emit }) {
    const id_unit = ref();
    const handleClick = () => {
      itemData.value.attention_date = null;
    };

    const blankData = {
      reference_number: null,
      id_unit: null,
      id_maintenance_company: null,
      attention_date: null,
      id_maintenance_template: null,
      id_maintenance_crew: null,
    };

    const dateStarClick = (val) => {
      itemData.value.attention_date = val;
    };

    const toast = useToast();
    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      id_unit.value = null;
    };

    // Normalizamos lo que venga de SelectPaginateUnit a un ID
    const resolveUnitId = () => {
      const unit = id_unit.value;
      if (!unit) return null;

      // Multiple
      if (Array.isArray(unit)) {
        return unit;
      }

      // Si viene número o string directo
      if (typeof unit === "number" || typeof unit === "string") {
        return unit;
      }

      if (typeof unit === "object") {
        // { value: 123 }
        if (unit.value !== undefined && unit.value !== null) {
          return unit.value;
        }
        // { id: 123 }
        if (unit.id !== undefined && unit.id !== null) {
          return unit.id;
        }
        // { unit: 123, method: 'edit' }
        if (unit.unit !== undefined && unit.unit !== null) {
          return unit.unit;
        }
      }

      return null;
    };

    const onSubmit = () => {
      const unitId = resolveUnitId();

      let dispatch;
      if (props.isAdd) {
        const payload = {
          ...itemData.value,
          id_unit: unitId,
        };

        dispatch = store.dispatch("service_orders/add", payload);
        id_unit.value = null;
      } else {
        const req = {
          id: itemData.value.id,
          params: {
            reference_number: itemData.value.reference_number,
            id_unit: unitId,
            id_maintenance_company: itemData.value.id_maintenance_company,
            attention_date: itemData.value.attention_date,
            id_maintenance_template: itemData.value.id_maintenance_template,
            id_maintenance_crew: itemData.value.id_maintenance_crew,
          },
        };
        dispatch = store.dispatch("service_orders/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response?.data?.message || "Error",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(
      resetData
    );

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit;

        // Le avisamos al SelectPaginateUnit que está en modo edición
        id_unit.value = props.itemEdit.id_unit;
        /*id_unit.value = {
          unit: props.itemEdit.id_unit,
          method: "edit",
        };*/

        itemData.value.id_maintenance_crew =
          props.itemEdit.id_maintenance_crew ||
          (props.itemEdit.maintenance_crew
            ? props.itemEdit.maintenance_crew.id
            : null);
      } else {
        resetData();
        itemData.value.reference_number = moment().format('DDMMYYYY_HHmm');
      }
    };

    return {
      id_unit,
      handleClick,
      dateStarClick,
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
    };
  },
};
</script>
